/**
 * HSERow — three-square HSE card row at the top of the Performance tab.
 *
 * Purely presentational: takes a pre-computed `HseBundle` from the engine
 * and renders one `HSECard` per entry. Owns the per-card expand state so
 * cards can open and close independently (multi-expand, per § 13 of the
 * spec — accordion mode is explicitly NOT what we want).
 *
 * The expanded drill-down panel is rendered BELOW the row of three cards,
 * not in place of any card — the three-square visual identity is preserved
 * across all states. (See mockups/hse_card_states.html State 5.)
 *
 * Spec: docs/features/Life4Earth_HSE_Feature_Spec.md § 7
 * Mockup: mockups/hse_card_states.html
 * Epic: DEV-822, Task: DEV-828
 */

import React, { useState, useCallback } from 'react';
import { HSECard } from '../HSECard/HSECard';
import type { HseBundle, HseSubKpi } from '../../kpi-engine/hse';
import type { HseCategory } from '../../kpi-engine/missionTypes/types';
import styles from './HSERow.module.css';

// ── Props ────────────────────────────────────────────────────────

export interface HSERowProps {
  /** Pre-computed HSE bundle from `computeHseBundle()`. */
  bundle: HseBundle;
  /** Called when the user clicks "+ Report" on a card. Parent opens the modal. */
  onReportIncident: (category: HseCategory) => void;
  /**
   * When true, render a pencil-to-configure icon on each card. Parent
   * usually computes from canAdminProject. (DEV-822)
   */
  canConfigure?: boolean;
  /**
   * Called when the user clicks the pencil on a card. Parent opens the
   * configuration tray with the wizard pre-loaded for that sub-KPI.
   * Receives the card so the parent can look up the underlying instance.
   */
  onConfigureCard?: (card: HseSubKpi) => void;
  /**
   * Optional renderer for the expanded drill-down panel for a given card.
   * Not provided in v1 wire-up — IncidentDrillDown lands as a follow-up.
   * When omitted, expand toggles a placeholder panel.
   */
  renderDrillDown?: (card: HseSubKpi, onClose: () => void) => React.ReactNode;
  /** Optional debug overlay on each card. */
  debugMode?: boolean;
}

// ── Component ────────────────────────────────────────────────────

export function HSERow({
  bundle,
  onReportIncident,
  canConfigure = false,
  onConfigureCard,
  renderDrillDown,
  debugMode = false,
}: HSERowProps) {
  // Per-card expand state, keyed by sub_kpi_guid. Independent — clicking
  // Health does NOT collapse Environment.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const close = useCallback((id: string) => {
    setExpandedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  if (bundle.cards.length === 0) {
    // No HSE cards configured for this project. Render nothing — the
    // default-provisioning task (DEV-835) ensures every project has 3
    // HSE cards, but during the rollout window some projects may not
    // have been backfilled yet.
    return null;
  }

  // DEV-822 / migration 039 — pin the visual order Health → Safety →
  // Environment regardless of insert timestamp, display_name, or array
  // order from the engine. Cards' meaning is global to the platform and
  // their sequence is part of the meaning (pedagogical: Health and
  // Safety first, Environment third). Unknown categories sort to the end.
  const HSE_CATEGORY_ORDER: Record<string, number> = {
    health: 0,
    safety: 1,
    environment: 2,
  };
  const orderedCards = [...bundle.cards].sort((a, b) => {
    const ka = HSE_CATEGORY_ORDER[String(a?.config?.hse_category ?? '').toLowerCase()];
    const kb = HSE_CATEGORY_ORDER[String(b?.config?.hse_category ?? '').toLowerCase()];
    const oa = Number.isFinite(ka) ? ka : 99;
    const ob = Number.isFinite(kb) ? kb : 99;
    return oa - ob;
  });

  return (
    <div className={styles.wrapper} data-testid="hse-row">
      <div className={styles.row}>
        {orderedCards.map((card) => (
          <HSECard
            key={card.sub_kpi_guid}
            category={card.config.hse_category}
            score={card.score}
            displayName={card.display_name}
            expanded={expandedIds.has(card.sub_kpi_guid)}
            onExpand={() => toggle(card.sub_kpi_guid)}
            onReportClick={() => onReportIncident(card.config.hse_category)}
            canConfigure={canConfigure}
            onConfigClick={() => onConfigureCard?.(card)}
            debugMode={debugMode}
          />
        ))}
      </div>

      {/* Expanded drill-down panels stack BELOW the row — one per
          currently-expanded card. Layout invariant: the row above is
          unchanged; expansion adds height below it. Uses orderedCards
          so an expanded panel sits under its source card. */}
      {orderedCards.map((card) => {
        if (!expandedIds.has(card.sub_kpi_guid)) return null;
        const onClose = () => close(card.sub_kpi_guid);
        return (
          <div
            key={`panel-${card.sub_kpi_guid}`}
            className={styles.panelSlot}
            data-color={
              card.score.score_0_100 === null
                ? 'green'
                : card.score.score_0_100 >= 100
                ? 'green'
                : card.score.score_0_100 >= 70
                ? 'yellow'
                : 'red'
            }
          >
            {renderDrillDown ? (
              renderDrillDown(card, onClose)
            ) : (
              <DefaultPanelPlaceholder card={card} onClose={onClose} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Placeholder panel ─────────────────────────────────────────────
// Renders when no `renderDrillDown` prop is supplied. This is a temporary
// fallback during rollout — DEV-829 (IncidentDrillDown) replaces it.

function DefaultPanelPlaceholder({
  card,
  onClose,
}: {
  card: HseSubKpi;
  onClose: () => void;
}) {
  const periodCount = card.score.hse?.period_count ?? 0;
  return (
    <div className={styles.placeholder}>
      <div className={styles.placeholderHead}>
        <strong>{card.display_name}</strong>
        <span className={styles.placeholderCount}>
          {periodCount} incident{periodCount === 1 ? '' : 's'} · drill-down
          panel coming soon (DEV-829)
        </span>
        <button
          className={styles.placeholderClose}
          onClick={onClose}
          type="button"
          aria-label="Close"
        >
          &times;
        </button>
      </div>
      {card.score.hse?.most_recent_title && (
        <div className={styles.placeholderRecent}>
          Most recent:{' '}
          <strong>{card.score.hse.most_recent_title}</strong>
          {card.score.hse.most_recent_severity && (
            <span> · {card.score.hse.most_recent_severity}</span>
          )}
          {card.score.hse.most_recent_issue_id && (
            <span> · {card.score.hse.most_recent_issue_id}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default HSERow;
